/**
 * Offline licences — for machines that will never reach the control plane.
 *
 * The flow is a round trip by hand: the machine prints a request with
 * `chi auth request`, somebody carries it here, the admin issues a licence
 * against a member's seat, and the downloaded file goes back to the machine for
 * `chi auth import`.
 *
 * Two things this page has to make plain, because nothing else will:
 *
 * - **The fingerprint.** The request is unsigned on purpose (the device key
 *   agrees, it does not sign), so what guards against being handed somebody
 *   else's request is a person comparing the fingerprint the machine printed
 *   with the one shown here, before issuing. It is read from the request itself
 *   — the machine's own rendering — rather than recomputed and hoped to agree.
 * - **Revocation does not reach these machines.** A revoked device keeps
 *   working until its licence runs out, so the table of what was issued and
 *   until when is the admin's whole remaining control, and says so.
 */

import { useMemo, useState } from 'react'
import { Link, Navigate } from 'react-router-dom'

import type { OfflineLicense } from '@/entities/types'
import {
  useIssueOfflineLicense,
  useMembers,
  useOfflineLicenses,
  useOrganization,
} from '@/shared/api/hooks'
import { downloadBlob } from '@/shared/api/client'
import { AppShell, PageHead } from '@/shared/ui/Chrome'
import {
  Badge,
  Button,
  Empty,
  ErrorNotice,
  Field,
  Loading,
  Notice,
  Panel,
  Select,
  formatDate,
  platformLabel,
} from '@/shared/ui/primitives'
import { NoWorkspace, useCurrentOrgId } from './Team'

/** The label `chi auth request` puts in front of its blob. */
const REQUEST_PREFIX = 'chi-offline-request-v1.'

/** What a machine says about itself in its request. */
export interface OfflineRequest {
  install_id: string
  display_name: string
  platform: string
  arch: string
  cli_version: string
  fingerprint: string
}

/**
 * Reads a pasted request, for showing it before anything is sent.
 *
 * The server parses it again and is the authority; this only exists so the
 * admin can see which machine and which fingerprint they are about to license.
 * Whitespace is dropped anywhere, as the server does, because a request arrives
 * pasted out of a terminal, a chat or a ticket, and any of those may wrap it.
 */
export function parseOfflineRequest(blob: string): OfflineRequest | { error: string } | null {
  const cleaned = blob.replace(/\s+/g, '')
  if (!cleaned) return null
  if (!cleaned.startsWith(REQUEST_PREFIX)) {
    return {
      error:
        'This does not look like an offline licence request. Run `chi auth request` on the machine and paste all of its output.',
    }
  }
  try {
    const encoded = cleaned.slice(REQUEST_PREFIX.length).replace(/-/g, '+').replace(/_/g, '/')
    const padded = encoded + '='.repeat((4 - (encoded.length % 4)) % 4)
    const text = new TextDecoder().decode(Uint8Array.from(atob(padded), (c) => c.charCodeAt(0)))
    const parsed = JSON.parse(text) as Partial<OfflineRequest>
    if (!parsed.install_id) return { error: 'The request names no installation.' }
    return {
      install_id: parsed.install_id,
      display_name: parsed.display_name ?? '',
      platform: parsed.platform ?? '',
      arch: parsed.arch ?? '',
      cli_version: parsed.cli_version ?? '',
      fingerprint: parsed.fingerprint ?? '',
    }
  } catch {
    return { error: 'The request is damaged. Copy it again, all of it.' }
  }
}

/** A file name somebody can recognise on a USB stick. */
function licenseFileName(machine: string): string {
  const slug = machine
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return `archis-offline-licence${slug ? `-${slug}` : ''}.json`
}

function daysLeft(iso: string): number {
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000)
}

export function OfflineLicenses() {
  const orgId = useCurrentOrgId()
  if (!orgId) return <NoWorkspace />
  return <OfflineLicensePage orgId={orgId} />
}

function OfflineLicensePage({ orgId }: { orgId: string }) {
  const { data: org } = useOrganization(orgId)
  const { data: licenses, isPending, error } = useOfflineLicenses(orgId)
  const { data: roster } = useMembers(orgId)

  const isEnterprise = org?.plan === 'enterprise'
  const canManage = Boolean(org?.capabilities['devices.manage'])

  if (org && !canManage) {
    return <Navigate to="/team" replace />
  }

  const emailOf = new Map((roster?.members ?? []).map((member) => [member.user_id, member.email]))

  return (
    <AppShell>
      <PageHead
        index="12"
        eyebrow="Workspace"
        title="Offline licences"
        subtitle="Licence files for machines that never reach the Archis servers."
      />

      <div className="stack" style={{ ['--gap' as string]: 'var(--s5)' }}>
        {org && !isEnterprise && (
          <Panel title="Archis Enterprise" index="!" tone="signal">
            <div className="stack" style={{ ['--gap' as string]: 'var(--s4)' }}>
              <p className="dim" style={{ margin: 0, maxWidth: '64ch' }}>
                Offline licences for air-gapped machines are part of{' '}
                <strong>Archis Enterprise</strong>. Issuing is disabled on the {org.plan} tier.
              </p>
              <div className="row" style={{ gap: 'var(--s3)', flexWrap: 'wrap' }}>
                <Link to="/pricing#enterprise">
                  <Button variant="primary">Contact Enterprise Sales</Button>
                </Link>
              </div>
            </div>
          </Panel>
        )}

        <IssuePanel orgId={orgId} disabled={!isEnterprise} />

        <Panel title="Issued licences" index="02" padded={false}>
          {isPending && <Loading label="Loading offline licences" />}
          <ErrorNotice error={error} />
          {licenses && licenses.length === 0 && (
            <Empty>No offline licences issued yet.</Empty>
          )}
          {licenses && licenses.length > 0 && (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Machine</th>
                    <th>Member</th>
                    <th>Platform</th>
                    <th>Expires</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {licenses.map((license) => (
                    <tr key={license.device_id} data-testid={`offline-licence-${license.device_id}`}>
                      <td>
                        {license.display_name || <span className="muted">unnamed</span>}
                        <div className="mono-note">chi {license.cli_version}</div>
                      </td>
                      <td>{emailOf.get(license.user_id) ?? <span className="muted">—</span>}</td>
                      <td className="mono-note">{platformLabel(license.platform, license.arch)}</td>
                      <td className="mono-note">{formatDate(license.expires_at)}</td>
                      <td>
                        <LicenseStatus license={license} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        <Panel title="How offline licences behave" index="03">
          <ul
            className="stack mono-note"
            style={{ ['--gap' as string]: 'var(--s2)', paddingLeft: 'var(--s5)', margin: 0 }}
          >
            <li>A licence is bound to the one machine that printed the request, and uses its member's seat.</li>
            <li>It never contacts the Archis servers. Renewing it is the same round trip: a new request, a new file.</li>
            <li>
              Revoking the device or the seat does not reach the machine. It keeps working until the
              licence expires, which is why this list is kept.
            </li>
            <li>Every licence issued is recorded in the audit log.</li>
          </ul>
        </Panel>
      </div>
    </AppShell>
  )
}

function LicenseStatus({ license }: { license: OfflineLicense }) {
  if (license.expired) return <Badge>expired</Badge>
  const left = daysLeft(license.expires_at)
  return (
    <div className="row" style={{ gap: 'var(--s2)', flexWrap: 'wrap' }}>
      {license.revoked ? (
        <Badge tone="coral">revoked · still valid</Badge>
      ) : (
        <Badge tone={left <= 14 ? 'amber' : 'signal'} dot>
          active
        </Badge>
      )}
      <span className="mono-note">
        {left} day{left === 1 ? '' : 's'} left
      </span>
    </div>
  )
}

function IssuePanel({ orgId, disabled }: { orgId: string; disabled: boolean }) {
  const { data: roster } = useMembers(orgId)
  const issue = useIssueOfflineLicense(orgId)

  const [blob, setBlob] = useState('')
  const [memberId, setMemberId] = useState('')
  const [confirmed, setConfirmed] = useState(false)
  const [issued, setIssued] = useState<{ name: string; expiresAt: string } | null>(null)

  const parsed = useMemo(() => parseOfflineRequest(blob), [blob])
  const machine = parsed && !('error' in parsed) ? parsed : null
  // A licence is months of access nothing can take back, so only members who
  // hold a seat today can receive one; the server refuses the rest anyway.
  const seated = (roster?.members ?? []).filter(
    (member) => member.status === 'active' && member.has_seat,
  )

  const reset = () => {
    setBlob('')
    setMemberId('')
    setConfirmed(false)
  }

  const submit = () => {
    if (!machine) return
    issue.mutate(
      { memberId, request: blob },
      {
        onSuccess: (result) => {
          const name = licenseFileName(machine.display_name)
          downloadBlob(
            new Blob([JSON.stringify(result.license, null, 2) + '\n'], { type: 'application/json' }),
            name,
          )
          setIssued({ name, expiresAt: result.device.expires_at })
          reset()
        },
      },
    )
  }

  return (
    <Panel title="Issue a licence" index="01">
      <div className="stack" style={{ ['--gap' as string]: 'var(--s4)' }}>
        {issued && (
          <Notice tone="ok">
            <span>
              Downloaded <code>{issued.name}</code>, valid until {formatDate(issued.expiresAt)}.
              Copy it to the machine and run <code>chi auth import {issued.name}</code>.
            </span>
          </Notice>
        )}

        <div className="term">
          <div className="term__bar">on the machine with no network</div>
          <div className="term__body">
            <div className="term__line">
              <span className="term__prompt">$</span> chi auth request
            </div>
            <div className="term__line term__dim">
              {'  '}prints a request and this machine&apos;s fingerprint. Bring the request here.
            </div>
          </div>
        </div>

        <Field label="Request" hint="All of the output of `chi auth request`, as printed.">
          <textarea
            className="input"
            rows={4}
            style={{ fontFamily: 'var(--mono)', wordBreak: 'break-all' }}
            placeholder={`${REQUEST_PREFIX}…`}
            value={blob}
            disabled={disabled}
            onChange={(event) => {
              setBlob(event.target.value)
              setConfirmed(false)
              setIssued(null)
            }}
            data-testid="offline-request"
          />
        </Field>

        {parsed && 'error' in parsed && <Notice tone="warn">{parsed.error}</Notice>}

        {machine && (
          <div className="stack" style={{ ['--gap' as string]: 'var(--s3)' }}>
            <dl className="mono-note" style={{ margin: 0 }}>
              <div>
                machine · <strong>{machine.display_name || 'unnamed'}</strong> ·{' '}
                {platformLabel(machine.platform, machine.arch)} · chi {machine.cli_version}
              </div>
              <div>
                fingerprint ·{' '}
                {machine.fingerprint ? (
                  <strong data-testid="offline-fingerprint">{machine.fingerprint}</strong>
                ) : (
                  <span className="muted">none — this machine sent no device key</span>
                )}
              </div>
            </dl>

            {!machine.fingerprint && (
              <Notice tone="warn">
                Without a device key the licence cannot carry encryption keys, so this machine will
                not open an encrypted index. Run <code>chi auth request</code> again on a current
                release.
              </Notice>
            )}

            <Field label="Member" hint="The licence uses this member's seat.">
              <Select
                value={memberId}
                onChange={(event) => setMemberId(event.target.value)}
                options={[
                  { value: '', label: seated.length ? 'Choose a member…' : 'No member holds a seat' },
                  ...seated.map((member) => ({ value: member.id, label: member.email })),
                ]}
                disabled={disabled || seated.length === 0}
              />
            </Field>

            {machine.fingerprint && (
              <label className="row" style={{ gap: 'var(--s2)', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={confirmed}
                  disabled={disabled}
                  onChange={(event) => setConfirmed(event.target.checked)}
                  data-testid="offline-fingerprint-confirm"
                />
                <span>
                  The machine printed the same fingerprint, <code>{machine.fingerprint}</code>.
                </span>
              </label>
            )}

            <ErrorNotice error={issue.error} />

            <div className="row" style={{ gap: 'var(--s3)' }}>
              <Button
                variant="primary"
                disabled={disabled || !memberId || (Boolean(machine.fingerprint) && !confirmed)}
                loading={issue.isPending}
                onClick={submit}
              >
                Issue and download
              </Button>
              <Button variant="ghost" onClick={reset}>
                Clear
              </Button>
            </div>
          </div>
        )}
      </div>
    </Panel>
  )
}
